import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

/**
 * Per-thread dialogue state for the quick-reply system.
 *
 * - `activeChoices[threadId]`: which choice list is currently shown.
 *   - When undefined → show the tree's root choices.
 *   - When set → show this set of follow-up choices (because the most recent
 *     NPC reply branched into specific follow-ups).
 * - `firedPairs[threadId]`: set of "<choiceId>:<responseIdx>" strings already
 *   shown so we never repeat the same NPC line twice in a row until the pool
 *   exhausts and resets.
 * - `usedOneShots[threadId]`: choiceIds that have been fired and were oneShot.
 */

interface DialogueStateStore {
  // Stack of follow-up choice IDs per thread. When NPC reply lands with
  // followUps, we stash the followUp choice array indexed by nodePath.
  // Simpler model: just remember the LAST set of followUp choices per thread.
  followUpsByThread: Record<string, FollowUpRef | undefined>;
  firedPairs: Record<string, string[]>;          // threadId -> ["choiceId:idx", …]
  usedOneShots: Record<string, string[]>;         // threadId -> [choiceId, …]
  // Threads where an NPC reply is currently scheduled. Drives the typing
  // indicator. Cleared when the reply lands or thread is reset.
  pendingReplyByThread: Record<string, boolean | undefined>;
  setFollowUps: (threadId: string, ref: FollowUpRef | undefined) => void;
  recordFired: (threadId: string, choiceId: string, responseIdx: number) => void;
  recordOneShot: (threadId: string, choiceId: string) => void;
  setPendingReply: (threadId: string, pending: boolean) => void;
  resetThread: (threadId: string) => void;
}

export interface FollowUpRef {
  // Identifies which follow-up choice array is currently active. Resolved at
  // render-time by re-walking the dialogue tree using these path keys.
  rootChoiceId: string;
  responseIdx: number;
}

export const useDialogue = create<DialogueStateStore>()(
  immer((set) => ({
    followUpsByThread: {},
    firedPairs: {},
    usedOneShots: {},
    pendingReplyByThread: {},
    setFollowUps: (threadId, ref) => set((s) => {
      s.followUpsByThread[threadId] = ref;
    }),
    recordFired: (threadId, choiceId, responseIdx) => set((s) => {
      const key = `${choiceId}:${responseIdx}`;
      const arr = s.firedPairs[threadId] ?? [];
      if (!arr.includes(key)) arr.push(key);
      s.firedPairs[threadId] = arr;
    }),
    recordOneShot: (threadId, choiceId) => set((s) => {
      const arr = s.usedOneShots[threadId] ?? [];
      if (!arr.includes(choiceId)) arr.push(choiceId);
      s.usedOneShots[threadId] = arr;
    }),
    setPendingReply: (threadId, pending) => set((s) => {
      if (pending) s.pendingReplyByThread[threadId] = true;
      else delete s.pendingReplyByThread[threadId];
    }),
    resetThread: (threadId) => set((s) => {
      s.followUpsByThread[threadId] = undefined;
      s.firedPairs[threadId] = [];
      s.usedOneShots[threadId] = [];
      delete s.pendingReplyByThread[threadId];
    }),
  })),
);

/**
 * Pick the next response variant for a choice, preferring unfired ones. When
 * all are fired, reset the fired list for that choice and pick fresh.
 */
export function pickResponseIdx(
  choiceId: string,
  numResponses: number,
  firedKeys: string[],
): number {
  if (numResponses === 0) return 0;
  const unfiredIdxs: number[] = [];
  for (let i = 0; i < numResponses; i++) {
    if (!firedKeys.includes(`${choiceId}:${i}`)) unfiredIdxs.push(i);
  }
  if (unfiredIdxs.length === 0) {
    // Pool exhausted — pick random and let recordFired silently overwrite
    return Math.floor(Math.random() * numResponses);
  }
  return unfiredIdxs[Math.floor(Math.random() * unfiredIdxs.length)]!;
}
